import React, { useState, useEffect } from 'react';
import { Modal, Pressable, Text, TextInput, View, StyleSheet, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaEntry } from '../../../../styles/theme';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import type { MealEntryType } from '../../../../redux/mealEntry';

interface Props {
  type: 'dining' | 'note';
  initial?: { place?: string; note?: string; text?: string } | null;
  dayLabel: string;
  onSave: (data: { place?: string; note?: string; text?: string }) => void;
  onCancel: () => void;
}

export function EntryFormModal({ type, initial, dayLabel, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const [place, setPlace] = useState(initial?.place || '');
  const [note, setNote] = useState(initial?.note || '');
  const [text, setText] = useState(initial?.text || '');

  const meta = ENTRY_META[type];
  const presets = t(
    type === 'dining' ? 'entryForm.diningPresets' : 'entryForm.notePresets',
    { returnObjects: true },
  ) as string[];

  const canSave = type === 'dining' ? true : text.trim().length > 0;

  const handleSave = () => {
    if (!canSave) return;
    if (type === 'dining') {
      onSave({
        place: place.trim() || t('entryForm.defaultPlace'),
        ...(note.trim() && { note: note.trim() }),
      });
    } else {
      onSave({ text: text.trim() });
    }
  };

  const handlePresetTap = (preset: string) => {
    if (type === 'dining') {
      setPlace(preset);
    } else {
      setText(preset);
    }
  };

  return (
    <Modal transparent visible onRequestClose={onCancel}>
      <Pressable style={styles.scrim} onPress={onCancel} />
      <View style={styles.container}>
        <View style={[styles.modal, { backgroundColor: Paprika.surfaceLowest }]}>
          {/* Header */}
          <View style={[styles.header, { backgroundColor: meta.tint }]}>
            <View style={[styles.iconSquare, { backgroundColor: Paprika.surfaceLowest }]}>
              <EntryIcon type={type} size={22} />
            </View>
            <View style={styles.headerText}>
              <Text style={styles.title}>{t(type === 'dining' ? 'entryForm.diningTitle' : 'entryForm.noteTitle')}</Text>
              <Text style={styles.subtitle}>{dayLabel}</Text>
            </View>
          </View>

          {/* Body */}
          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {type === 'dining' ? (
              <>
                {/* Place field */}
                <View style={styles.field}>
                  <Text style={styles.label}>{t('entryForm.whereLabel')}</Text>
                  <TextInput
                    style={styles.input}
                    placeholder={t('entryForm.placePlaceholder')}
                    placeholderTextColor={Paprika.textMuted}
                    value={place}
                    onChangeText={setPlace}
                    autoFocus
                  />
                </View>

                {/* Place presets */}
                <View style={styles.presetsContainer}>
                  {presets.map((preset) => (
                    <Pressable
                      key={preset}
                      style={styles.preset}
                      onPress={() => handlePresetTap(preset)}
                    >
                      <Text style={styles.presetText}>{preset}</Text>
                    </Pressable>
                  ))}
                </View>

                {/* Note field */}
                <View style={styles.field}>
                  <Text style={styles.label}>{t('entryForm.noteLabel')}</Text>
                  <TextInput
                    style={[styles.input, styles.multilineInput]}
                    placeholder={t('entryForm.notePlaceholder')}
                    placeholderTextColor={Paprika.textMuted}
                    value={note}
                    onChangeText={setNote}
                    multiline
                  />
                </View>
              </>
            ) : (
              <>
                {/* Text field */}
                <View style={styles.field}>
                  <Text style={styles.label}>{t('entryForm.eatingLabel')}</Text>
                  <TextInput
                    style={styles.input}
                    placeholder={t('entryForm.textPlaceholder')}
                    placeholderTextColor={Paprika.textMuted}
                    value={text}
                    onChangeText={setText}
                    autoFocus
                  />
                </View>

                {/* Note presets */}
                <View style={styles.presetsContainer}>
                  {presets.map((preset) => (
                    <Pressable
                      key={preset}
                      style={styles.preset}
                      onPress={() => handlePresetTap(preset)}
                    >
                      <Text style={styles.presetText}>{preset}</Text>
                    </Pressable>
                  ))}
                </View>
              </>
            )}
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable style={styles.ghostBtn} onPress={onCancel}>
              <Text style={styles.ghostBtnText}>{t('common.cancel')}</Text>
            </Pressable>
            <Pressable
              style={[styles.primaryBtn, !canSave && styles.primaryBtnDisabled]}
              onPress={handleSave}
              disabled={!canSave}
            >
              <Text style={[styles.primaryBtnText, !canSave && styles.primaryBtnTextDisabled]}>
                {t('common.save')}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(27, 28, 26, 0.34)',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    width: 420,
    borderRadius: 20,
    shadowColor: 'rgba(82, 68, 60, 0.14)',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 16,
    overflow: 'hidden',
    maxHeight: '90%',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
  },
  iconSquare: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 20,
    lineHeight: 26,
    color: Paprika.textPrimary,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    gap: 16,
  },
  field: {
    gap: 8,
  },
  label: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textPrimary,
    backgroundColor: Paprika.bgSunk,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 0,
  },
  multilineInput: {
    minHeight: 80,
    textAlignVertical: 'top' as const,
  },
  presetsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  preset: {
    backgroundColor: Paprika.secondaryContainer,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  presetText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.onSecondaryContainer,
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    marginTop: 8,
  },
  ghostBtn: {
    flex: 1,
    paddingVertical: 11,
    paddingHorizontal: 16,
    borderRadius: 24,
    borderWidth: 1.5,
    borderColor: 'rgba(215, 194, 184, 0.4)',
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ghostBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  primaryBtn: {
    flex: 1,
    paddingVertical: 11,
    paddingHorizontal: 16,
    borderRadius: 24,
    backgroundColor: Paprika.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryBtnDisabled: {
    backgroundColor: Paprika.surfaceHighest,
    opacity: 0.5,
  },
  primaryBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.onPrimary,
  },
  primaryBtnTextDisabled: {
    color: Paprika.textSecondary,
  },
});
